// This file is auto-generated by @hey-api/openapi-ts

import {
  client,
  type Options,
  formDataBodySerializer,
} from '@hey-api/client-fetch';
import type {
  ListAlignmentsData,
  ListAlignmentsError,
  ListAlignmentsResponse,
  GetAlignmentData,
  GetAlignmentError,
  GetAlignmentResponse,
  DeleteAlignmentData,
  DeleteAlignmentError,
  DeleteAlignmentResponse,
  ProjectToStationData,
  ProjectToStationError,
  ProjectToStationResponse,
  TransformToCoordinateData,
  TransformToCoordinateError,
  TransformToCoordinateResponse,
  TransformToPlotSetData,
  TransformToPlotSetError,
  TransformToPlotSetResponse,
  GetTrimBimData,
  GetTrimBimError,
  GetTrimBimResponse,
  GetTrimBimExportSettingsData,
  GetTrimBimExportSettingsError,
  GetTrimBimExportSettingsResponse,
  SetTrimBimExportSettingsData,
  SetTrimBimExportSettingsError,
  SetTrimBimExportSettingsResponse,
  ExportTrimBimData,
  ExportTrimBimError,
  ExportTrimBimResponse,
  CreateSourceFileData,
  CreateSourceFileError,
  CreateSourceFileResponse,
  ListSourceFilesData,
  ListSourceFilesError,
  ListSourceFilesResponse,
  GetSourceFileData,
  GetSourceFileError,
  GetSourceFileResponse,
  DeleteSourceFileData,
  DeleteSourceFileError,
  DeleteSourceFileResponse,
  GetSourceFileContentData,
  GetSourceFileContentError,
  GetSourceFileContentResponse,
  CheckHealthData,
  CheckHealthError,
  CheckHealthResponse,
  ListRailsData,
  ListRailsError,
  ListRailsResponse,
  GetRailData,
  GetRailError,
  GetRailResponse,
  GetRailInfoFromStationData,
  GetRailInfoFromStationError,
  GetRailInfoFromStationResponse,
  ProjectPositionToAlignmentStationData,
  ProjectPositionToAlignmentStationError,
  ProjectPositionToAlignmentStationResponse,
  ListSettingsData,
  ListSettingsError,
  ListSettingsResponse,
  GetSettingsData,
  GetSettingsError,
  GetSettingsResponse,
  CreateOrUpdateSettingData,
  CreateOrUpdateSettingError,
  CreateOrUpdateSettingResponse,
  DeleteSettingData,
  DeleteSettingError,
  DeleteSettingResponse,
  ListUnitOfMeasuresData,
  ListUnitOfMeasuresError,
  ListUnitOfMeasuresResponse,
} from './types.gen';

/**
 * List all Alignments associated with the tenant
 * ### List Alignments
 *
 * This endpoint allows you to list all Alignments associated with the tenant.
 */
export const listAlignments = (options: Options<ListAlignmentsData>) => {
  return (options?.client ?? client).get<
    ListAlignmentsResponse,
    ListAlignmentsError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/alignments',
  });
};

/**
 * Get an Alignment with the given id
 * ### Get an Alignment
 *
 * This endpoint allows you to get an Alignment by its id.
 */
export const getAlignment = (options: Options<GetAlignmentData>) => {
  return (options?.client ?? client).get<
    GetAlignmentResponse,
    GetAlignmentError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/alignments/{id}',
  });
};

/**
 * Delete an Alignment
 * ### Delete an Alignment
 *
 * This endpoint allows you to delete an Alignment.
 *
 * ### Notes
 *
 * - Deleting an Alignment will permanently remove it.
 */
export const deleteAlignment = (options: Options<DeleteAlignmentData>) => {
  return (options?.client ?? client).delete<
    DeleteAlignmentResponse,
    DeleteAlignmentError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/alignments/{id}',
  });
};

/**
 * Project a coordinate to the alignment station
 */
export const projectToStation = (options: Options<ProjectToStationData>) => {
  return (options?.client ?? client).post<
    ProjectToStationResponse,
    ProjectToStationError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/alignments/{id}/projection/stations',
  });
};

/**
 * Transform an alignment station to a coordinate
 */
export const transformToCoordinate = (
  options: Options<TransformToCoordinateData>
) => {
  return (options?.client ?? client).post<
    TransformToCoordinateResponse,
    TransformToCoordinateError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/alignments/{id}/transformation/coordinates',
  });
};

/**
 * Transform alignment stations to coordinate plotsets
 * ### Sample response:
 * ```
 * {
 * "xAxis": { "name": "Station", "axisType": "Numeric", "start": 40261.9726294436, "decimals": null,	"unitType": null, "unit": null, "unitSymbol": null },
 * "components": [
 * { "name": "X"}, {"name": "Y"}, {"name": "Z"},
 * {"name": "ForwardX"}, {"name": "ForwardY"}, {"name": "ForwardZ"},
 * {"name": "UpX"}, {"name": "UpY"}, {"name": "UpZ"},
 * {"name": "RightX"}, {"name": "RightY"}, {"name": "RightZ"}
 * ],
 * "values": [
 * [0.0,2801523.14309176,1136474.62820567,1241.18543876,0.80813128,0.58493786,-0.06907635,0.05595642,0.04050212,0.99761138,0.5863384,-0.81006622,0.0],
 * [1.0,2801523.9531526,1136475.21454018,1241.11619747,0.80813128,0.58493786,-0.06907635,0.05595642,0.04050212,0.99761138,0.5863384,-0.81006622,0.0],
 * ...
 * ```
 *
 * Multiple components are produced for each point on the trajectory.
 * The first component is the xAxis `Station` value which is a normalized chainage starting at 0. Obtain the real chainage by adding xAxis.start to each `Station` value.</br>
 * The rest of the components are:</br>
 * `X Y Z` are the coordinates of the point.</br>
 * `ForwardX ForwardY ForwardZ` a unit vector indicating direction forward at the point.</br>
 * `UpX UpY UpZ` a unit vector indicating direction up at the point.</br>
 * `RightX RightY RightZ` a unit vector indicating direction right at the point.</br>
 */
export const transformToPlotSet = (
  options: Options<TransformToPlotSetData>
) => {
  return (options?.client ?? client).post<
    TransformToPlotSetResponse,
    TransformToPlotSetError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/alignments/{id}/transformation/coordinates/plotsets',
  });
};

/**
 * Get an alignment in the format of a TrimBim file
 * ### Export Alignments as TrimBim
 *
 * This endpoint provides basic export functionality using the alignment's stored settings.
 *
 * **⚠️ Recommendation:** For advanced features (segment tagging, custom settings) or production use,
 * use the **POST /trimBim/export** endpoint instead. The POST endpoint provides a stateless operation
 * that avoids race conditions in multi-user scenarios by accepting all export parameters in the request body.
 */
export const getTrimBim = (options: Options<GetTrimBimData>) => {
  return (options?.client ?? client).get<GetTrimBimResponse, GetTrimBimError>({
    ...options,
    url: '/tenants/{tenantTrn}/alignments/{id}/trimBim',
  });
};

/**
 * Get the export settings for a specific alignment
 * ### Export Settings for Alignments
 *
 * This endpoint allows you to get the export settings for a specific Alignment
 *
 * ---
 *
 * **ℹ️ Note:** If you need to export with custom settings without storing them,
 * use the **POST /trimBim/export** endpoint instead. It accepts all export settings
 * directly in the request body, eliminating the need to retrieve or store settings separately.
 * This is particularly useful for stateless integrations and multi-user scenarios.
 */
export const getTrimBimExportSettings = (
  options: Options<GetTrimBimExportSettingsData>
) => {
  return (options?.client ?? client).get<
    GetTrimBimExportSettingsResponse,
    GetTrimBimExportSettingsError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/alignments/{id}/trimBim/settings',
  });
};

/**
 * Set the export settings for a specific alignment
 * ### Update Export Settings for Alignments
 *
 * This endpoint allows you to set the export settings for a specific Alignment
 *
 * ---
 *
 * **⚠️ Important:** Storing settings can lead to race conditions in multi-user environments.
 * Consider using the **POST /trimBim/export** endpoint instead, which accepts all settings
 * directly in the request body for a thread-safe, stateless operation. This eliminates the
 * need to store settings and prevents conflicts when multiple users export simultaneously.
 *
 * Use this PUT endpoint only if you need to persist default settings for future exports.
 */
export const setTrimBimExportSettings = (
  options: Options<SetTrimBimExportSettingsData>
) => {
  return (options?.client ?? client).put<
    SetTrimBimExportSettingsResponse,
    SetTrimBimExportSettingsError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/alignments/{id}/trimBim/settings',
  });
};

/**
 * Export an alignment as TrimBim with custom settings
 * ### Export Alignment as TrimBim with Custom Settings
 *
 * This endpoint provides a **stateless export operation** that avoids race conditions
 * in multi-user scenarios by accepting all export parameters in the request body.
 *
 * **Key Features:**
 * - **Thread-Safe**: No dependency on stored settings - all parameters in request body
 * - **Flexible Settings**: Provide custom export settings or use alignment defaults
 * - **Segment Tagging**: Split alignment into tagged segments (e.g., by scan runs)
 * - **Backward Compatible**: Original GET endpoint remains unchanged
 *
 * **Request Body Parameters:**
 * - `centerLineOnly`: If true, exports only center line (default: false)
 * - `segmentTags`: Optional array to split alignment into tagged sections
 * - `exportSettings`: Custom export settings (colors, label spacing, etc.)
 * - If null, uses alignment's stored settings
 * - If alignment has no stored settings, uses system defaults
 *
 * **Example Request:**
 * ```json
 * {
 * "centerLineOnly": false,
 * "segmentTags": [
 * { "beginStation": 0, "endStation": 100, "tag": "Run1" },
 * { "beginStation": 100, "endStation": 200, "tag": "Run2" }
 * ],
 * "exportSettings": {
 * "colorName": "Green",
 * "stationLabelSpacing": 10.0
 * },
 * "uomPreference": {
 * "linearUnit": "ft"
 * }
 * }
 * ```
 *
 * **Important Notes:**
 * - All input values (beginStation, endStation, segmentLength, etc.) must be in **meters**.
 * - The `uomPreference` only controls the formatting of station labels in the TrimBim file.
 * - If `uomPreference` is null, station labels will be formatted in meters with 3 decimals.
 */
export const exportTrimBim = (options: Options<ExportTrimBimData>) => {
  return (options?.client ?? client).post<
    ExportTrimBimResponse,
    ExportTrimBimError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/alignments/{id}/trimBim/export',
  });
};

/**
 * Create a Source File.
 * ### Create a Source File
 *
 * The source file can be of type
 * * LandXml
 * * Txl
 * * Rxl
 * * Track
 * * Csv
 *
 * Every source file creates one or more alignments.
 */
export const createSourceFile = (options: Options<CreateSourceFileData>) => {
  return (options?.client ?? client).post<
    CreateSourceFileResponse,
    CreateSourceFileError
  >({
    ...options,
    ...formDataBodySerializer,
    url: '/tenants/{tenantTrn}/sourceFiles',
  });
};

/**
 * List all Source Files associated with the tenant
 * ### List Source Files
 *
 * This endpoint allows you to list all Source Files associated with the tenant.
 *
 * Supported values for the `States` parameter are:</br>
 * * `Ingested` - Source files that have been processed and ingested.
 * * `Pending` - Source files that are pending processing.
 * * `Failed` - Source files that have failed processing.
 */
export const listSourceFiles = (options: Options<ListSourceFilesData>) => {
  return (options?.client ?? client).get<
    ListSourceFilesResponse,
    ListSourceFilesError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/sourceFiles',
  });
};

/**
 * Get a Source File with the given id
 * ### Get a Source File
 *
 * This endpoint allows you to get a Source File by its id.
 */
export const getSourceFile = (options: Options<GetSourceFileData>) => {
  return (options?.client ?? client).get<
    GetSourceFileResponse,
    GetSourceFileError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/sourceFiles/{id}',
  });
};

/**
 * Delete a Source File with the given id
 * ### Delete a Source File
 *
 * This endpoint allows you to delete a Source File by its id.
 */
export const deleteSourceFile = (options: Options<DeleteSourceFileData>) => {
  return (options?.client ?? client).delete<
    DeleteSourceFileResponse,
    DeleteSourceFileError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/sourceFiles/{id}',
  });
};

/**
 * Download the original content of a Source File
 * ### Get Source File Content
 *
 * Streams the originally uploaded file content. Only available when the source file state is `Ingested`.
 */
export const getSourceFileContent = (
  options: Options<GetSourceFileContentData>
) => {
  return (options?.client ?? client).get<
    GetSourceFileContentResponse,
    GetSourceFileContentError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/sourceFiles/{id}/content',
  });
};

/**
 * Check the health of the services
 * ### Check Health
 *
 * This endpoint will check the health of the external services and return the status of each service.
 *
 * ### Notes
 *
 * - This endpoint does not require authentication.
 * - The region parameter must be one of the supported Trimble Connect regions.
 */
export const checkHealth = (options?: Options<CheckHealthData>) => {
  return (options?.client ?? client).get<CheckHealthResponse, CheckHealthError>(
    {
      ...options,
      url: '/health/{region}',
    }
  );
};

/**
 * List all Rails associated with the tenant
 * ### List Rails
 *
 * This endpoint allows you to list all Rails associated with the tenant.
 */
export const listRails = (options: Options<ListRailsData>) => {
  return (options?.client ?? client).get<ListRailsResponse, ListRailsError>({
    ...options,
    url: '/tenants/{tenantTrn}/rails',
  });
};

/**
 * Get a Rail with the given id
 * ### Get a Rail
 *
 * This endpoint allows you to get a Rail by its id.
 */
export const getRail = (options: Options<GetRailData>) => {
  return (options?.client ?? client).get<GetRailResponse, GetRailError>({
    ...options,
    url: '/tenants/{tenantTrn}/rails/{id}',
  });
};

/**
 * Get detailed Rail information from a station
 * ### Get Rail information at a station
 *
 *
 * This endpoint allows you to get detailed Rail information from a specific station.</br>
 * **Note:** You must either provide the internal station or the global station.</br>
 */
export const getRailInfoFromStation = (
  options: Options<GetRailInfoFromStationData>
) => {
  return (options?.client ?? client).get<
    GetRailInfoFromStationResponse,
    GetRailInfoFromStationError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/rails/{id}/railInfo',
  });
};

/**
 * Project a coordinate to a rail station
 * ### Project to a rail station
 *
 * Project a given point coordinate to the station on the specified rail alignment.
 */
export const projectPositionToAlignmentStation = (
  options: Options<ProjectPositionToAlignmentStationData>
) => {
  return (options?.client ?? client).post<
    ProjectPositionToAlignmentStationResponse,
    ProjectPositionToAlignmentStationError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/rails/{id}/projection/stations',
  });
};

/**
 * List all Settings associated with the tenant
 * ### List Settings
 *
 * This endpoint allows you to list all Settings associated with the tenant.
 */
export const listSettings = (options: Options<ListSettingsData>) => {
  return (options?.client ?? client).get<
    ListSettingsResponse,
    ListSettingsError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/settings',
  });
};

/**
 * Get a Setting with the given key
 * ### Get a Setting
 *
 * This endpoint allows you to get a Setting by its key.
 */
export const getSettings = (options: Options<GetSettingsData>) => {
  return (options?.client ?? client).get<GetSettingsResponse, GetSettingsError>(
    {
      ...options,
      url: '/tenants/{tenantTrn}/settings/{key}',
    }
  );
};

/**
 * Create or Update a Setting with the given key
 * ### Create or Update a Setting
 *
 * This endpoint allows you to create or update a Setting by its key.
 */
export const createOrUpdateSetting = (
  options: Options<CreateOrUpdateSettingData>
) => {
  return (options?.client ?? client).put<
    CreateOrUpdateSettingResponse,
    CreateOrUpdateSettingError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/settings/{key}',
  });
};

/**
 * Delete a Setting with the given key
 * ### Delete a Setting
 *
 * This endpoint allows you to delete a Setting by its key.
 *
 * **Note:** This operation will permanently remove the setting from the database.
 */
export const deleteSetting = (options: Options<DeleteSettingData>) => {
  return (options?.client ?? client).delete<
    DeleteSettingResponse,
    DeleteSettingError
  >({
    ...options,
    url: '/tenants/{tenantTrn}/settings/{key}',
  });
};

/**
 * List all Unit of Measures
 * ### List Unit of Measures
 *
 * This endpoint allows you to list all Unit of Measures.
 */
export const listUnitOfMeasures = (
  options?: Options<ListUnitOfMeasuresData>
) => {
  return (options?.client ?? client).get<
    ListUnitOfMeasuresResponse,
    ListUnitOfMeasuresError
  >({
    ...options,
    url: '/uom/{region}',
  });
};
